import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Divider,
  Button,
} from '@chakra-ui/react';
import {
  FaGlobe,
  FaComments,
  FaSmile,
  FaChartBar,
} from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  EXAMPLE_JUNK_REMOVAL_NASHVILLE,
  EXAMPLE_YEAR_DATA,
  EXAMPLE_TRAFFIC_HISTORY,
} from '../data/exampleCompetitorMapData';
import DemoBusinessReviewModal from './DemoBusinessReviewModal';
import BusinessIntelCard from './core/BusinessIntelCard';

const COLOR_SCALES = {
  top: '#22C55E',
  high: '#84CC16',
  mid: '#EAB308',
  low: '#F97316',
  bottom: '#EF4444',
  noData: '#6B7280',
};

const COLOR_MODES = {
  growth: {
    label: 'Review Growth',
    icon: FaChartBar,
    getMetric: (b) => {
      const counts = b.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(counts);
      if (months.length === 0) return 0;
      return months.reduce((sum, m) => sum + (counts[m] || 0), 0);
    },
  },
  sentiment: {
    label: 'Customer Sentiment',
    icon: FaSmile,
    getMetric: (b) => b.rating_avg || 0,
    getColor: (val) => {
      if (val >= 4.5) return COLOR_SCALES.top;
      if (val >= 4.0) return COLOR_SCALES.high;
      if (val >= 3.5) return COLOR_SCALES.mid;
      if (val >= 3.0) return COLOR_SCALES.low;
      return COLOR_SCALES.bottom;
    },
  },
  totals: {
    label: 'Review Totals',
    icon: FaComments,
    getMetric: (b) => b.rating_count || 0,
  },
  traffic: {
    label: 'Website Traffic',
    icon: FaGlobe,
    getMetric: (b) => b.traffic_data?.metrics?.estimated_monthly_visitors || 0,
  },
};

function computePercentileColors(businesses, getMetric) {
  const values = businesses.map(getMetric).filter(v => v > 0).sort((a, b) => a - b);
  if (values.length === 0) return () => COLOR_SCALES.noData;
  const p20 = values[Math.floor(values.length * 0.2)] || 0;
  const p40 = values[Math.floor(values.length * 0.4)] || 0;
  const p60 = values[Math.floor(values.length * 0.6)] || 0;
  const p80 = values[Math.floor(values.length * 0.8)] || 0;
  return (val) => {
    if (val <= 0) return COLOR_SCALES.noData;
    if (val >= p80) return COLOR_SCALES.top;
    if (val >= p60) return COLOR_SCALES.high;
    if (val >= p40) return COLOR_SCALES.mid;
    if (val >= p20) return COLOR_SCALES.low;
    return COLOR_SCALES.bottom;
  };
}

function getPercentileLevels(businesses, getMetric, label) {
  const values = businesses.map(getMetric).filter(v => v > 0).sort((a, b) => a - b);
  if (values.length === 0) return [{ label: `No ${label} data`, color: COLOR_SCALES.noData }];
  const p80 = values[Math.floor(values.length * 0.8)] || 0;
  const p60 = values[Math.floor(values.length * 0.6)] || 0;
  const p40 = values[Math.floor(values.length * 0.4)] || 0;
  const p20 = values[Math.floor(values.length * 0.2)] || 0;
  const fmt = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));
  return [
    { label: `${fmt(p80)}+`, color: COLOR_SCALES.top },
    { label: `${fmt(p60)}–${fmt(p80)}`, color: COLOR_SCALES.high },
    { label: `${fmt(p40)}–${fmt(p60)}`, color: COLOR_SCALES.mid },
    { label: `${fmt(p20)}–${fmt(p40)}`, color: COLOR_SCALES.low },
    { label: `< ${fmt(p20)}`, color: COLOR_SCALES.bottom },
  ];
}

function createNeonDotSvg(color) {
  const uid = Math.random().toString(36).slice(2, 8);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
    <defs>
      <radialGradient id="glow-outer-${uid}" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="${color}" stop-opacity="0.35"/>
        <stop offset="60%" stop-color="${color}" stop-opacity="0.12"/>
        <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="glow-mid-${uid}" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="${color}" stop-opacity="0.7"/>
        <stop offset="50%" stop-color="${color}" stop-opacity="0.25"/>
        <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="glow-core-${uid}" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="white" stop-opacity="0.95"/>
        <stop offset="40%" stop-color="${color}" stop-opacity="0.9"/>
        <stop offset="100%" stop-color="${color}" stop-opacity="0.5"/>
      </radialGradient>
      <filter id="blur-${uid}" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="3"/>
      </filter>
    </defs>
    <circle cx="24" cy="24" r="22" fill="url(#glow-outer-${uid})"/>
    <circle cx="24" cy="24" r="14" fill="url(#glow-mid-${uid})" filter="url(#blur-${uid})"/>
    <circle cx="24" cy="24" r="7" fill="url(#glow-core-${uid})"/>
    <circle cx="24" cy="24" r="3.5" fill="white" opacity="0.9"/>
  </svg>`;
}

function createLabeledPinIcon(business, color) {
  const displayName = business.name.length > 22
    ? business.name.substring(0, 22) + '\u2026'
    : business.name;
  const safeDisplayName = displayName
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;">
      ${createNeonDotSvg(color)}
      <div style="display:flex;align-items:center;gap:5px;background:rgba(10,10,26,0.88);border:1px solid ${color}44;border-radius:8px;padding:4px 10px;margin-top:2px;box-shadow:0 0 12px ${color}55, 0 0 4px ${color}33;max-width:200px;backdrop-filter:blur(6px);">
        <span style="font-size:12.5px;font-weight:600;color:#f0f0f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:170px;line-height:1.3;text-shadow:0 0 8px ${color}88, 0 0 2px ${color}44;">${safeDisplayName}</span>
      </div>
    </div>
  `;

  return L.divIcon({
    html,
    className: 'competitor-map-pin',
    iconSize: [200, 80],
    iconAnchor: [100, 40],
  });
}

const DOWNTOWN_NASHVILLE = [36.162, -86.781];
const DEFAULT_ZOOM = 13;

function MapAutoFit() {
  const map = useMap();
  useEffect(() => {
    map.setView(DOWNTOWN_NASHVILLE, DEFAULT_ZOOM);
  }, [map]);
  return null;
}



function ColorModeSelector({ colorMode, setColorMode, legendLevels }) {
  const modes = Object.entries(COLOR_MODES);
  return (
    <Box
      position="absolute"
      bottom={4}
      left={4}
      zIndex={1000}
      bg="rgba(15, 23, 42, 0.92)"
      border="1px solid"
      borderColor="gray.600"
      borderRadius="xl"
      px={{ base: 2.5, md: 4 }}
      py={{ base: 2, md: 3 }}
      backdropFilter="blur(12px)"
      maxW={{ base: "165px", md: "250px" }}
      boxShadow="0 4px 24px rgba(0, 0, 0, 0.3)"
    >
      <VStack align="stretch" spacing={{ base: 1.5, md: 2.5 }}>
        <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
          Color By
        </Text>
        <VStack align="stretch" spacing={1}>
          {modes.map(([key, mode]) => (
            <Button
              key={key}
              size="sm"
              variant={colorMode === key ? 'solid' : 'ghost'}
              colorScheme={colorMode === key ? 'blue' : 'gray'}
              justifyContent="flex-start"
              leftIcon={<Icon as={mode.icon} boxSize={{ base: 3, md: 3.5 }} />}
              onClick={() => setColorMode(key)}
              fontWeight={colorMode === key ? 'bold' : 'normal'}
              color={colorMode === key ? 'white' : 'gray.400'}
              _hover={{ color: 'white', bg: colorMode === key ? undefined : 'whiteAlpha.100' }}
              borderRadius="md"
              px={{ base: 2, md: 2.5 }}
              h={{ base: "26px", md: "32px" }}
              fontSize={{ base: "xs", md: "sm" }}
            >
              {mode.label}
            </Button>
          ))}
        </VStack>
        <Divider borderColor="gray.600" display={{ base: 'none', md: 'block' }} />
        <VStack align="stretch" spacing={1} display={{ base: 'none', md: 'flex' }}>
          {legendLevels.map((level, i) => (
            <HStack key={i} spacing={2}>
              <Box w={3} h={3} borderRadius="full" bg={level.color} flexShrink={0}
                boxShadow={`0 0 6px ${level.color}, 0 0 10px ${level.color}88`} />
              <Text mb={0} color="gray.400" fontSize="xs">{level.label}</Text>
            </HStack>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
}

function LandingCompetitorMap() {
  const [colorMode, setColorMode] = useState('growth');
  const [hoveredBusiness, setHoveredBusiness] = useState(null);
  const [hoveredTrafficData, setHoveredTrafficData] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const yearDataByUri = useMemo(() => {
    const map = {};
    EXAMPLE_YEAR_DATA.forEach(yd => { map[yd.maps_uri] = yd; });
    return map;
  }, []);

  const enrichedBusinesses = useMemo(() => {
    return EXAMPLE_JUNK_REMOVAL_NASHVILLE.map(business => {
      const yearInfo = yearDataByUri[business.maps_uri];
      return {
        ...business,
        historicalData: yearInfo?.data || business.historicalData || {},
        insight: yearInfo?.insight || business.insight || '',
        lat: business.latitude,
        lng: business.longitude,
      };
    });
  }, [yearDataByUri]);

  const colorFn = useMemo(() => {
    const mode = COLOR_MODES[colorMode];
    if (mode.getColor) return mode.getColor;
    return computePercentileColors(enrichedBusinesses, mode.getMetric);
  }, [colorMode, enrichedBusinesses]);

  const legendLevels = useMemo(() => {
    const mode = COLOR_MODES[colorMode];
    if (colorMode === 'sentiment') {
      return [
        { label: '4.5+ Stars', color: COLOR_SCALES.top },
        { label: '4.0+ Stars', color: COLOR_SCALES.high },
        { label: '3.5+ Stars', color: COLOR_SCALES.mid },
        { label: '3.0+ Stars', color: COLOR_SCALES.low },
        { label: '< 3.0 Stars', color: COLOR_SCALES.bottom },
      ];
    }
    return getPercentileLevels(enrichedBusinesses, mode.getMetric, mode.label);
  }, [colorMode, enrichedBusinesses]);

  const handleMarkerClick = useCallback((business) => {
    setSelectedBusiness(business);
    setIsModalOpen(true);
  }, []);

  return (
    <Box ref={containerRef} position="relative">
      <Box
        position="relative"
        h={{ base: "500px", md: "650px", lg: "700px" }}
        borderRadius="2xl"
        overflow="hidden"
        bg="#1a1a2e"
        border="2px solid"
        borderColor="gray.700"
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
        opacity={isVisible ? 1 : 0}
        transform={isVisible ? 'translateY(0)' : 'translateY(30px)'}
        transition="opacity 0.8s ease-out, transform 0.8s ease-out"
        sx={{
          '.leaflet-container': {
            background: '#0a0a1a',
            height: '100%',
            width: '100%',
            borderRadius: '16px',
          },
          '.leaflet-tile-pane': {
            filter: 'brightness(0.55) contrast(1.1) saturate(0.6)',
          },
          '.competitor-map-pin': {
            background: 'transparent',
            border: 'none',
          },
          '.leaflet-control-zoom': {
            border: 'none !important',
          },
          '.leaflet-control-zoom a': {
            background: '#1E293B !important',
            color: '#94A3B8 !important',
            border: '1px solid #334155 !important',
            borderRadius: '8px !important',
            width: '36px !important',
            height: '36px !important',
            lineHeight: '36px !important',
            fontSize: '18px !important',
          },
          '.leaflet-control-zoom a:hover': {
            background: '#334155 !important',
            color: '#E2E8F0 !important',
          },
          '.leaflet-control-attribution': {
            background: 'rgba(26, 26, 46, 0.85) !important',
            color: '#64748B !important',
            fontSize: '10px !important',
            padding: '2px 6px !important',
            borderRadius: '4px 0 0 0 !important',
          },
          '.leaflet-control-attribution a': {
            color: '#60A5FA !important',
          },
        }}
      >
        <ColorModeSelector
          colorMode={colorMode}
          setColorMode={setColorMode}
          legendLevels={legendLevels}
        />

        <MapContainer
          center={DOWNTOWN_NASHVILLE}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          scrollWheelZoom={false}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
            maxZoom={19}
          />
          <MapAutoFit />
          {enrichedBusinesses.map((business, index) => {
            const metric = COLOR_MODES[colorMode].getMetric(business);
            const pinColor = colorFn(metric);
            const bizTrafficData = EXAMPLE_TRAFFIC_HISTORY[business.maps_uri] || null;

            return (
              <Marker
                key={`${business.name}-${index}`}
                position={[business.lat, business.lng]}
                icon={createLabeledPinIcon(business, pinColor)}
                eventHandlers={{
                  click: () => handleMarkerClick(business),
                  mouseover: () => {
                    setHoveredBusiness(business);
                    setHoveredTrafficData(bizTrafficData);
                  },
                  mouseout: () => {
                    setHoveredBusiness(null);
                  },
                }}
              />
            );
          })}
        </MapContainer>

        {/* Hover Card Overlay */}
        <Box
          position="absolute"
          top="50%"
          right={{ base: 2, md: 4 }}
          zIndex={1001}
          pointerEvents="none"
          maxH="calc(100% - 60px)"
          overflowY="auto"
          opacity={hoveredBusiness ? 1 : 0}
          transform={hoveredBusiness ? 'translateY(-50%)' : 'translateX(20px) translateY(-50%)'}
          transition="opacity 0.2s ease-out, transform 0.2s ease-out"
          display={{ base: 'none', md: 'block' }}
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bg: 'gray.300', borderRadius: 'full' },
          }}
        >
          {hoveredBusiness && (
            <BusinessIntelCard
              business={hoveredBusiness}
              trafficData={hoveredTrafficData}
              variant="hover"
              glowOnHover={false}
            />
          )}
        </Box>

        <Text
          position="absolute"
          bottom={2}
          right={2}
          zIndex={1000}
          color="gray.500"
          fontSize="xs"
          bg="rgba(15, 23, 42, 0.85)"
          px={3}
          py={1}
          borderRadius="md"
          backdropFilter="blur(8px)"
        >
          Click any pin for full analysis
        </Text>
      </Box>

      <DemoBusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        business={selectedBusiness}
        presentation="map"
      />
    </Box>
  );
}

export default LandingCompetitorMap;
